import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * DatePicker Component - Dual Input Mode
 * 
 * UX REQUIREMENTS:
 * - User can type a date directly (e.g., "2026-02-10" or "10/02/2026")
 * - Date picker remains available as assistive control
 * - Typed input is parsed and validated on blur
 * - Invalid dates show validation message
 * - Manual typing wins over picker if conflict
 */
export function DatePicker({ 
  value, 
  onChange, 
  placeholder = "YYYY-MM-DD",
  className,
  disabled = false,
  minDate,
  maxDate,
  ...props 
}) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [error, setError] = React.useState("");
  
  // Parse string date to Date object
  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    try {
      const parsed = parse(value, "yyyy-MM-dd", new Date());
      return isValid(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }, [value]);

  // Sync input value with prop value
  React.useEffect(() => {
    if (dateValue) {
      setInputValue(format(dateValue, "yyyy-MM-dd"));
      setError("");
    } else if (!value) {
      setInputValue("");
    }
  }, [dateValue, value]);

  // Handle direct text input
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setError(""); // Clear error while typing
  };

  // Validate and apply on blur
  const handleInputBlur = () => {
    if (!inputValue.trim()) {
      onChange("");
      setError("");
      return;
    }

    // Try multiple date formats
    const formats = ["yyyy-MM-dd", "dd/MM/yyyy", "MM/dd/yyyy", "dd-MM-yyyy"];
    let parsed = null;
    
    for (const fmt of formats) {
      try {
        const result = parse(inputValue, fmt, new Date());
        if (isValid(result)) {
          parsed = result;
          break;
        }
      } catch {
        continue;
      }
    }

    if (parsed && isValid(parsed)) {
      const isoDate = format(parsed, "yyyy-MM-dd");
      onChange(isoDate);
      setInputValue(isoDate);
      setError("");
    } else {
      setError("Invalid date. Use YYYY-MM-DD format.");
    }
  };

  // Handle calendar selection
  const handleSelect = (date) => {
    if (date) {
      const isoDate = format(date, "yyyy-MM-dd");
      onChange(isoDate);
      setInputValue(isoDate);
      setError("");
    } else {
      onChange("");
      setInputValue("");
    }
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="flex">
        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "bg-surface-input border-border text-foreground rounded-r-none",
            "focus:ring-primary focus:border-primary",
            error && "border-red-500",
            className
          )}
          {...props}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "px-3 border border-l-0 border-border rounded-r-md",
                "bg-surface-elevated hover:bg-surface-hover transition-colors",
                "text-muted-foreground hover:text-foreground",
                "focus:outline-none focus:ring-2 focus:ring-primary"
              )}
              title="Open calendar"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-auto p-0 bg-surface-base border-border rounded-md shadow-lg" 
            align="end"
          >
            <Calendar
              mode="single"
              selected={dateValue}
              onSelect={handleSelect}
              disabled={(date) => {
                const checkDate = new Date(date);
                checkDate.setHours(0, 0, 0, 0);
                
                if (minDate) {
                  const min = new Date(minDate);
                  min.setHours(0, 0, 0, 0);
                  if (checkDate < min) return true;
                }
                if (maxDate) {
                  const max = new Date(maxDate);
                  max.setHours(0, 0, 0, 0);
                  if (checkDate > max) return true;
                }
                return false;
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
}

export default DatePicker;
